import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Send, ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import Footer from '@/shared/components/Footer';
import Navbar from '@/shared/components/Navbar';
import { Alert, AlertDescription } from '@/shared/components/ui/Alert';
import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Label } from '@/shared/components/ui/Label';
import { Textarea } from '@/shared/components/ui/Textarea';
import { useI18n } from '@/shared/contexts/I18nProvider';
import { useToast } from '@/shared/contexts/ToastContext';
import type { ContactValidationCopy } from '@/shared/i18n';

const namePattern = /^[\p{L}' -]+$/u;

const createContactSchema = (validation: ContactValidationCopy) =>
  z.object({
    firstName: z
      .string()
      .trim()
      .min(2, validation.firstNameMin)
      .max(80, validation.firstNameMax)
      .regex(namePattern, validation.firstNamePattern),
    lastName: z
      .string()
      .trim()
      .min(2, validation.lastNameMin)
      .max(80, validation.lastNameMax)
      .regex(namePattern, validation.lastNamePattern),
    email: z
      .string()
      .trim()
      .email(validation.emailInvalid)
      .max(254, validation.emailMax),
    message: z
      .string()
      .trim()
      .min(5, validation.messageMin)
      .max(2000, validation.messageMax),
    company: z.string().optional()
  });

type ContactFormValues = {
  company?: string;
  email: string;
  firstName: string;
  lastName: string;
  message: string;
};

const googleMailerUrl = (
  import.meta.env.VITE_GOOGLE_MAILER_URL ??
  import.meta.env.VITE_GOOGLE_EMAILER_URL
)?.trim();

const fieldClassName =
  'mt-2 border-border-primary bg-background-primary/80 focus-visible:border-accent-primary';

const errorClassName = 'mt-2 text-sm leading-5 text-accent-error';

const ContactPage = () => {
  const { content, localizedPath } = useI18n();
  const contact = content.contact;
  const contactSchema = useMemo(
    () => createContactSchema(contact.validation),
    [contact.validation]
  );
  const { notify } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      message: '',
      company: ''
    }
  });

  const onSubmit = async (values: ContactFormValues) => {
    if (values.company?.trim()) {
      reset();
      notify('success', contact.notifications.received);
      return;
    }

    if (!googleMailerUrl) {
      notify('error', contact.notifications.mailerMissing);
      return;
    }

    const payload = new URLSearchParams({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      message: values.message,
      submittedAt: new Date().toISOString(),
      source: window.location.href
    });

    try {
      await fetch(googleMailerUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: payload
      });

      reset();
      notify('success', contact.notifications.sent);
    } catch {
      notify('error', contact.notifications.failed);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary text-text-primary antialiased">
      <Navbar />

      <main className="pt-32 sm:pt-28">
        <section className="border-b border-border-muted bg-background-primary py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:items-start lg:px-8">
            <div>
              <Badge
                variant="outline"
                className="mb-5 border-accent-primary/30 bg-background-card/70 font-mono uppercase tracking-[0.18em] text-accent-primary"
              >
                {contact.badge}
              </Badge>
              <h1 className="max-w-4xl text-5xl font-light tracking-tight sm:text-6xl">
                {contact.title}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-text-secondary">
                {contact.description}
              </p>

              <div className="mt-8 grid gap-3">
                {contact.supportPoints.map(point => (
                  <div
                    key={point}
                    className="flex items-center gap-3 rounded-md border border-border-muted bg-background-card px-4 py-3 text-sm text-text-secondary"
                  >
                    <ShieldCheck className="h-5 w-5 shrink-0 text-accent-primary" />
                    {point}
                  </div>
                ))}
              </div>

              <Button
                asChild
                variant="outline"
                className="mt-8 w-fit gap-2 bg-transparent font-mono"
              >
                <Link
                  to={localizedPath('/')}
                  state={{ scrollToSection: 'platform' }}
                >
                  {contact.explorePlatform}
                  <ButtonArrow className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="rounded-lg border border-border-primary bg-background-card p-6 shadow-sm sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-primary text-text-inverse">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-2xl font-light">{contact.formTitle}</h2>
                </div>
              </div>

              {!googleMailerUrl && (
                <Alert variant="warning" className="mt-6">
                  <AlertDescription>
                    {contact.mailerWarningBeforeCode}{' '}
                    <code>VITE_GOOGLE_MAILER_URL</code>{' '}
                    {contact.mailerWarningAfterCode}
                  </AlertDescription>
                </Alert>
              )}

              <form
                className="mt-8 space-y-5"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <div
                  className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden"
                  aria-hidden="true"
                >
                  <Label htmlFor="company">{contact.labels.company}</Label>
                  <Input
                    id="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...register('company')}
                  />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="firstName">
                      {contact.labels.firstName}
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      autoComplete="given-name"
                      className={fieldClassName}
                      aria-invalid={errors.firstName ? 'true' : 'false'}
                      aria-describedby={
                        errors.firstName ? 'firstName-error' : undefined
                      }
                      {...register('firstName')}
                    />
                    {errors.firstName && (
                      <p id="firstName-error" className={errorClassName}>
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="lastName">{contact.labels.lastName}</Label>
                    <Input
                      id="lastName"
                      type="text"
                      autoComplete="family-name"
                      className={fieldClassName}
                      aria-invalid={errors.lastName ? 'true' : 'false'}
                      aria-describedby={
                        errors.lastName ? 'lastName-error' : undefined
                      }
                      {...register('lastName')}
                    />
                    {errors.lastName && (
                      <p id="lastName-error" className={errorClassName}>
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{contact.labels.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    className={fieldClassName}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p id="email-error" className={errorClassName}>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="message">{contact.labels.message}</Label>
                  <Textarea
                    id="message"
                    rows={7}
                    className={`${fieldClassName} min-h-44`}
                    aria-invalid={errors.message ? 'true' : 'false'}
                    aria-describedby={
                      errors.message ? 'message-error' : undefined
                    }
                    {...register('message')}
                  />
                  {errors.message && (
                    <p id="message-error" className={errorClassName}>
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  loading={isSubmitting}
                  className="w-full gap-2 font-mono sm:w-auto"
                >
                  {contact.sendMessage}
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
